/**
 * DocuSign Agent — provider-specific signing-flow API surface.
 *
 * Per docs/code-notes/INDEX-DOCUSIGN-PLACEMENT.md §4 this lives as a
 * sibling of documentAgent.js (NOT inside it) so it can be deleted
 * verbatim if cutthe6.com ever swaps providers again.
 *
 * Backend contracts: docs/architecture/DOCUSIGN_MIGRATION_DESIGN.md §12
 *   - POST /api/documents/{id}/signing-url
 *   - POST /api/documents/{id}/signers/{sig_id}/resend-email
 *   - GET  /api/documents/{id}/certificate
 *   - POST /api/documents/{id}/void  (re-exported from documentAgent)
 */

import { useCallback, useState } from 'react';
import APIAgent, { API_BASE_URL } from './apiAgent';
import DocumentAPI from './documentAgent';

/**
 * Typed error raised when the backend returns 429 from the resend endpoint.
 * Carries the `retry_after_seconds` hint from the API response so the caller
 * can surface a human-friendly retry hint or disable the affected control.
 */
export class RateLimitError extends Error {
  constructor(message, { retryAfterSeconds = null, detail = null } = {}) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
    this.detail = detail;
  }
}

/**
 * Mint a short-lived signing URL for the current user on a DocuSign envelope,
 * or surface the "remote — check your email" message.
 * Per ADD §8: no signature_id in the URL — the server resolves the caller's
 * row from the JWT, which makes IDOR structurally impossible.
 *
 * @param {number|string} documentId
 * @returns {Promise<{
 *   mode: 'embedded' | 'remote',
 *   signing_url?: string,
 *   message?: string,
 *   last_sent_at?: string | null,
 *   resend_count?: number,
 *   resend_window_resets_at?: string | null,
 *   expires_in?: number,
 * }>}
 */
export async function fetchSigningUrl(documentId) {
  try {
    return await APIAgent.request(
      `/api/documents/${documentId}/signing-url`,
      'POST',
    );
  } catch (err) {
    console.error('docusignAgent.fetchSigningUrl failed:', err);
    throw err;
  }
}

/**
 * Fetch backend send-readiness gate status + blocker reasons.
 *
 * @param {number|string} documentId
 * @returns {Promise<{
 *   document_id: number,
 *   is_ready: boolean,
 *   reasons: string[],
 *   required_document_types: string[],
 *   bundle_document_ids: number[],
 *   bundle_documents: Array<object>,
 * }>}
 */
export async function fetchSendReadiness(documentId) {
  try {
    return await APIAgent.request(
      `/api/documents/${documentId}/send-readiness`,
      'GET',
    );
  } catch (err) {
    console.error('docusignAgent.fetchSendReadiness failed:', err);
    throw err;
  }
}

/**
 * Ask the backend to ask DocuSign to resend the signing email to one signer.
 * Maps a 429 response to a typed RateLimitError so callers can render the
 * cap-hit toast without string-matching the error message.
 *
 * @param {number|string} documentId
 * @param {number|string} signatureId
 * @returns {Promise<void>}
 */
export async function resendSigningEmail(documentId, signatureId) {
  try {
    return await APIAgent.request(
      `/api/documents/${documentId}/signers/${signatureId}/resend-email`,
      'POST',
    );
  } catch (err) {
    const message = String(err?.message || '');
    // APIAgent surfaces FastAPI's `detail` string verbatim in err.message;
    // 429 messages typically include "Resend limit" or "rate limit". The
    // backend also returns a `retry_after_seconds` field in the JSON body,
    // but APIAgent has already discarded it by this point, so we re-attempt
    // a raw fetch only on suspected rate-limit failures to recover it.
    if (/limit|429|too many/i.test(message)) {
      let retryAfterSeconds = null;
      try {
        const probe = await fetch(
          `${API_BASE_URL}/api/documents/${documentId}/signers/${signatureId}/resend-email`,
          { method: 'POST', credentials: 'include' },
        );
        if (probe.status === 429) {
          const headerHint = probe.headers.get('Retry-After');
          if (headerHint && !Number.isNaN(Number(headerHint))) {
            retryAfterSeconds = Number(headerHint);
          }
          try {
            const body = await probe.json();
            if (body && typeof body.retry_after_seconds === 'number') {
              retryAfterSeconds = body.retry_after_seconds;
            }
          } catch (_) { /* no body */ }
        }
      } catch (_) { /* probe is best-effort */ }
      throw new RateLimitError(message, { retryAfterSeconds, detail: message });
    }
    console.error('docusignAgent.resendSigningEmail failed:', err);
    throw err;
  }
}

/**
 * Download the DocuSign Certificate of Completion PDF for a completed
 * envelope. Triggers a browser download, mirroring downloadDocument().
 *
 * @param {number|string} documentId
 * @returns {Promise<void>}
 */
export async function fetchCertificate(documentId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/documents/${documentId}/certificate`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      let detail = `Certificate download failed: ${response.status}`;
      try {
        const body = await response.json();
        if (body?.detail) detail = body.detail;
      } catch (_) { /* no body */ }
      throw new Error(detail);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificate_of_completion_${documentId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('docusignAgent.fetchCertificate failed:', err);
    throw err;
  }
}

/**
 * Void a document (re-exported so call sites only depend on docusignAgent
 * for signing-flow concerns; the underlying endpoint is unchanged).
 * @param {number|string} documentId
 * @param {string} reason
 */
export function voidDocument(documentId, reason) {
  return DocumentAPI.voidDocument(documentId, reason);
}

/**
 * Send a document for signature via the DocuSign-aware /send endpoint.
 * Body shape per ADD §12.1.
 *
 * @param {number|string} documentId
 * @param {{
 *   signers: Array<{
 *     signer_id: number,
 *     role?: string,
 *     signing_mode: 'embedded' | 'remote',
 *   }>,
 *   (Emailed WI spouses are verified by DocuSign Phone Authentication on the
 *   number from their partner's marital-status confirmation — the client no
 *   longer sends an access_code.)
 *   sender_completeness_ack?: boolean,
 *   void_previous_revision?: boolean,
 * }} payload
 */
export async function sendDocumentEnvelope(documentId, payload) {
  try {
    return await APIAgent.request(
      `/api/documents/${documentId}/send`,
      'POST',
      payload,
    );
  } catch (err) {
    console.error('docusignAgent.sendDocumentEnvelope failed:', err);
    throw err;
  }
}

/**
 * React hook bundling the DocuSign call surface with standard
 * loading / error / data state per copilot-instructions guidance.
 */
export const useDocuSign = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const wrap = useCallback(async (fn) => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err?.message || 'DocuSign request failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchSendReadiness: useCallback(
      (id) => wrap(() => fetchSendReadiness(id)),
      [wrap],
    ),
    fetchSigningUrl: useCallback((id) => wrap(() => fetchSigningUrl(id)), [wrap]),
    resendSigningEmail: useCallback(
      (id, sigId) => wrap(() => resendSigningEmail(id, sigId)),
      [wrap],
    ),
    fetchCertificate: useCallback((id) => wrap(() => fetchCertificate(id)), [wrap]),
    voidDocument: useCallback(
      (id, reason) => wrap(() => voidDocument(id, reason)),
      [wrap],
    ),
    sendDocumentEnvelope: useCallback(
      (id, payload) => wrap(() => sendDocumentEnvelope(id, payload)),
      [wrap],
    ),
  };
};

const docusignAgent = {
  fetchSendReadiness,
  fetchSigningUrl,
  resendSigningEmail,
  fetchCertificate,
  voidDocument,
  sendDocumentEnvelope,
  RateLimitError,
};

export default docusignAgent;
